import { useMutation } from '@tanstack/react-query'
import { destroyCookie, parseCookies, setCookie } from 'nookies'
import { createContext, type ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { getProfile } from '@/api/user/get-profile'
import { loginClient } from '@/api/user/sign-in'
import { api } from '@/services/axios'

type User = {
  id: number
  nome: string
  email: string
  refreshToken: string
  permissions: number[]
  role: string
}

type signInCredentials = {
  email: string
  password: string
}

type AuthContextData = {
  signIn(credentials: signInCredentials): Promise<void>
  user?: User
  isAuthenticated: boolean
}

type AuthProviderProps = {
  children: ReactNode
}

export const AuthContext = createContext({} as AuthContextData)

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate()
  const [user, setUser] = useState<User>()
  const isAuthenticated = !!user
  const { mutateAsync: loginClientFn } = useMutation({
    mutationFn: loginClient,
  })
  const { mutateAsync: getProfileFn } = useMutation({
    mutationFn: getProfile,
  })

  useEffect(() => {
    const {
      'reactAuth.token': token,
      'reactAuth.email': email,
      'reactAuth.refreshToken': refreshToken,
    } = parseCookies()

    if (token && email) {
      getProfileFn({ email })
        .then((data) => {
          const userData: User = {
            id: data.id,
            nome: data.nome,
            email: data.email,
            role: data.role,
            permissions: data.permissions,
            refreshToken,
          }
          setUser(userData)
        })
        .catch(() => {
          destroyCookie(undefined, 'reactAuth.token')
          destroyCookie(undefined, 'reactAuth.refreshToken')
          destroyCookie(undefined, 'reactAuth.email')
          destroyCookie(undefined, 'reactAuth.id')

          navigate('/sign-in')
        })
    }
  }, [getProfileFn, navigate])

  async function signIn({ email, password }: signInCredentials) {
    try {
      const response = await loginClientFn({
        email,
        password,
      })

      const { nome, token, refreshToken, permissions, role, id } = response.data

      setCookie(undefined, 'reactAuth.token', token, {
        expires: new Date('2238-01-01'),
        path: '/',
      })
      setCookie(undefined, 'reactAuth.refreshToken', refreshToken, {
        expires: new Date('2238-01-01'),
        path: '/',
      })
      setCookie(undefined, 'reactAuth.email', email, {
        expires: new Date('2238-01-01'),
        path: '/',
      })
      setCookie(undefined, 'reactAuth.id', id, {
        expires: new Date('2238-01-01'),
        path: '/',
      })

      setUser({ nome, email, refreshToken, permissions, role, id })

      api.defaults.headers.Authorization = `Bearer ${token}`

      toast.success('Sucesso: Login efetuado!!')
      navigate(`/`)
    } catch (error) {
      toast.error('Erro: Email ou Senha inválidos!!')
    }
  }
  return (
    <AuthContext.Provider value={{ signIn, isAuthenticated, user }}>
      {children}
    </AuthContext.Provider>
  )
}
